import React from "react";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Menu,
  MenuItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { GroupContainer } from "../../../common/components";

/**
 * Card "Estado de Firmas" de Edition. Vive en su propio archivo (no en los forms
 * generados) para minimizar los puntos de contacto con el código generado: los
 * forms Update/View lo invocan en pocas líneas, sin duplicar ~90 líneas de JSX.
 * Es autocontenido y se decopla del tipo del store recibiendo props explícitas.
 */

const SIGNATURE_STATUS_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  completed: { bg: "#e8f5e9", color: "#2e7d32", label: "Completado" },
  sentToAuthors: { bg: "#fff3e0", color: "#e65100", label: "Enviado a Autores" },
  sentToEditors: { bg: "#e3f2fd", color: "#1565c0", label: "Enviado a Editores" },
  expired: { bg: "#fce4ec", color: "#c62828", label: "Expirado" },
  sent: { bg: "#fff3e0", color: "#e65100", label: "Enviado" },
  delivered: { bg: "#fff3e0", color: "#e65100", label: "Entregado" },
  voided: { bg: "#fce4ec", color: "#c62828", label: "Anulado" },
  declined: { bg: "#fce4ec", color: "#c62828", label: "Rechazado" },
};

const RECIPIENT_STATUS_ICONS: Record<string, { icon: string; bg: string; color: string }> = {
  signed: { icon: "✓", bg: "#e8f5e9", color: "#2e7d32" },
  pending: { icon: "⏳", bg: "#fff3e0", color: "#e65100" },
  sent: { icon: "⏳", bg: "#fff3e0", color: "#e65100" },
  delivered: { icon: "⏳", bg: "#fff3e0", color: "#e65100" },
  declined: { icon: "✗", bg: "#fce4ec", color: "#c62828" },
  authFailed: { icon: "✗", bg: "#fce4ec", color: "#c62828" },
  bounced: { icon: "✗", bg: "#fce4ec", color: "#c62828" },
};

const SignatureStatusChip: React.FC<{ status: string | null }> = ({ status }) => {
  if (!status) return <Typography variant="body2" color="text.secondary">-</Typography>;
  const config = SIGNATURE_STATUS_COLORS[status] || { bg: "#f5f5f5", color: "#616161", label: status };
  return <Chip size="small" label={config.label} sx={{ backgroundColor: config.bg, color: config.color }} />;
};

const RESENDABLE_STATUSES = new Set(["pending", "sent", "delivered", "bounced"]);

const RecipientChip: React.FC<{
  recipient: { name: string; email: string; status: string };
  onResend?: (email: string) => void;
  /** Explicación cuando el chip es inerte (filas pre-envío, p.ej. la DJCT). */
  inertHint?: string;
}> = ({ recipient, onResend, inertHint }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const config = RECIPIENT_STATUS_ICONS[recipient.status] || { icon: "?", bg: "#f5f5f5", color: "#616161" };
  // Sin onResend (filas pre-envío: aún no hay email que reenviar) el chip es inerte.
  const canResend = !!onResend && RESENDABLE_STATUSES.has(recipient.status);

  const chip = (
    <Chip
      size="small"
      label={`${config.icon} ${recipient.name}`}
      onClick={canResend ? (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget) : undefined}
      sx={{
        backgroundColor: config.bg,
        color: config.color,
        ...(canResend && { cursor: "pointer", "&:hover": { opacity: 0.85 } }),
      }}
    />
  );

  return (
    <>
      {!canResend && inertHint ? (
        <Tooltip title={inertHint} placement="top" arrow>
          {chip}
        </Tooltip>
      ) : (
        chip
      )}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => { setAnchorEl(null); onResend?.(recipient.email); }}>
          <ListItemText primary="Reenviar email de firma" secondary={recipient.email} />
        </MenuItem>
      </Menu>
    </>
  );
};

const RecipientsCell: React.FC<{
  recipients: { name: string; email: string; status: string }[];
  onResend?: (email: string) => void;
  inertHint?: string;
}> = ({ recipients, onResend, inertHint }) =>
  recipients.length === 0 ? (
    <Typography variant="body2" color="text.secondary">-</Typography>
  ) : (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {recipients.map((r, idx) => (
        <RecipientChip key={idx} recipient={r} onResend={onResend} inertHint={inertHint} />
      ))}
    </Box>
  );

export interface SignatureStatusRow {
  editionDocumentationId: string;
  contractName: string;
  documentType?: string | null;
  signaturePhase?: string | null;
  documentStatus?: string | null;
  recipients: { name: string; email: string; status: string; order?: number | null }[];
}

/**
 * La DJCT se auto-envía a firmar recién cuando se completan TODOS los
 * contratos (su fecha debe coincidir con la del ejemplar de edición). Hasta
 * entonces no tiene esignatureDocument: en vez del "-" genérico, se explica.
 */
const isDjctRow = (row: SignatureStatusRow) =>
  row.documentType === "djct" || row.contractName.includes("DJCT-");

export const EditionSignatureStatusCard: React.FC<{
  signatureStatuses: SignatureStatusRow[];
  documentationStateValue: string;
  expanded: boolean;
  onToggle: () => void;
  onResend: (editionDocumentationId: string, email: string) => void;
  // Recovery (opcional): si se pasan, muestra el botón "Re-verificar firmas" en
  // sentForSignature y esperandoFirmaDjct. Inerte donde no se cableen.
  canRecheck?: boolean;
  onRecheck?: () => void;
  recheckLoading?: boolean;
}> = observer(({
  signatureStatuses,
  documentationStateValue,
  expanded,
  onToggle,
  onResend,
  canRecheck = false,
  onRecheck,
  recheckLoading = false,
}) => {
  if (signatureStatuses.length === 0 || documentationStateValue === "docsGenerated") return null;

  return (
    <GroupContainer id="signatureStatus" title="Estado de Firmas" expanded={expanded} toggleExpand={onToggle}>
      <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Contrato</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Firmantes (Autores)</TableCell>
              <TableCell>Firmantes (Editores)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {signatureStatuses.map((status) => (
              <TableRow key={status.editionDocumentationId}>
                <TableCell>{status.contractName}</TableCell>
                <TableCell>
                  {isDjctRow(status) && !status.documentStatus && !status.signaturePhase ? (
                    <Chip
                      size="small"
                      label="Esperando firma de contratos"
                      sx={{ backgroundColor: "#e3f2fd", color: "#1565c0" }}
                    />
                  ) : (
                    <SignatureStatusChip status={status.signaturePhase ?? status.documentStatus ?? null} />
                  )}
                </TableCell>
                <TableCell>
                  <RecipientsCell
                    recipients={status.recipients.filter((r) => (r.order ?? 1) <= 1)}
                    onResend={status.documentStatus ? (email) => onResend(status.editionDocumentationId, email) : undefined}
                    inertHint={!status.documentStatus ? "Aún no se envió el email de firma — no hay nada que reenviar." : undefined}
                  />
                </TableCell>
                <TableCell>
                  <RecipientsCell
                    recipients={status.recipients.filter((r) => (r.order ?? 1) >= 2)}
                    onResend={status.documentStatus ? (email) => onResend(status.editionDocumentationId, email) : undefined}
                    inertHint={!status.documentStatus ? (isDjctRow(status) ? "La DJCT se enviará a firmar al completarse los contratos — todavía no hay email que reenviar." : "Aún no se envió el email de firma — no hay nada que reenviar.") : undefined}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {canRecheck && onRecheck &&
        ["sentForSignature", "esperandoFirmaDjct"].includes(documentationStateValue) && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={recheckLoading}
            onClick={onRecheck}
            startIcon={recheckLoading ? <CircularProgress size={16} /> : undefined}
          >
            Re-verificar firmas
          </Button>
        </Box>
      )}
      </>
    </GroupContainer>
  );
});
